// Ventas -> Bolsas de soporte (D82, D94): horas de soporte contratadas por cliente, su saldo y lo que se ha prestado.
// Solo los 3 planes de QuickBooks (Emergency, Support 6, Support 12); las de Notion quedan como "Anterior". Diseno
// "lista y detalle" que eligio Johan: a la izquierda los clientes por "Atender ya / Activas / Vencidas", a la derecha
// el detalle de la bolsa. Los consumos de casos se descuentan solos al registrar horas (con minimo y recargo).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';

import {
  ADICIONAL_S12,
  bolsas,
  borrarMovimiento,
  comprarAdicionales,
  crearBolsaPlan,
  crearMovimiento,
  esAdicional,
  HORA_ADICIONAL,
  horarioSugerido,
  HORARIOS,
  minutosACobrar,
  movimientos,
  nombrePlan,
  PLANES,
  saldoDe,
  vigenciaDe,
  type Bolsa,
  type Horario,
  type Movimiento,
  type Plan,
  type Saldo,
} from '../../api/bolsas';
import { horasTexto } from '../../api/proyectos';
import { listAll, update } from '../../api/rest';
import { useSession } from '../../auth/session';
import { Icon } from '../../lib/icons';
import { BotonExcel } from '../../ui/BotonExcel';
import { Empty, ErrorBox, Kpi, Pill, Skeleton } from '../../ui/bits';
import { Dialog } from '../../ui/Dialog';
import { SelectorFecha } from '../../ui/SelectorFecha';
import { toast } from '../../ui/toasts';

export type BolsaConSaldo = Bolsa & { movs: Movimiento[]; saldo: Saldo };

export const useBolsas = () =>
  useQuery({
    queryKey: ['bolsas'],
    queryFn: async (): Promise<BolsaConSaldo[]> => {
      const [b, m] = await Promise.all([bolsas(), movimientos()]);
      return b.map((x) => {
        const movs = m.filter((y) => y.bolsaId === x.id);
        return { ...x, movs, saldo: saldoDe(movs, x) };
      });
    },
    staleTime: 60_000,
  });

const TONO = { verde: 'ok', amarillo: 'warn', rojo: 'bad' } as const;
const DIA = 86_400_000;
const fechaLarga = (t: string | number) => new Date(t).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Bogota' });
const fechaCorta = (t: string | number) => new Date(t).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', timeZone: 'America/Bogota' });
const usd = (n: number) => `US$${n.toLocaleString('es-CO')}`;
const hoyIso = () => new Date().toISOString().slice(0, 10);
const aIso = (d: string) => new Date(`${d}T12:00:00`).toISOString();

const textoSaldo = (s: Saldo, plan: Plan) =>
  s.vencida ? 'Vencida' : s.restantes <= 0 ? (plan === 'SUPPORT_12' ? 'Sin horas este mes' : 'Sin saldo') : `Quedan ${horasTexto(s.restantes)}${plan === 'SUPPORT_12' ? ' este mes' : ''}`;

export function PillSaldo({ saldo, plan = 'ANTERIOR' }: { saldo: Saldo; plan?: Plan }) {
  return (
    <Pill tone={TONO[saldo.semaforo]} dot title={`${horasTexto(saldo.recargados)} ${plan === 'SUPPORT_12' ? 'este mes' : 'contratadas'} · ${horasTexto(saldo.consumidos)} usadas`}>
      {plan !== 'ANTERIOR' ? `${nombrePlan(plan)} · ` : ''}
      {textoSaldo(saldo, plan)}
    </Pill>
  );
}

/** Saldo de la bolsa vigente de una empresa (para el caso y la ficha), o nada si no tiene. */
export function SaldoEmpresa({ empresaId }: { empresaId: string | null | undefined }) {
  const b = useBolsas();
  if (!empresaId) return null;
  const vigente = bolsaDeEmpresa(b.data ?? [], empresaId);
  return vigente ? <PillSaldo saldo={vigente.saldo} plan={vigente.plan} /> : null;
}

/** La bolsa que se muestra de una empresa: la vigente que vence primero, o la mas reciente. */
export function bolsaDeEmpresa(lista: BolsaConSaldo[], empresaId: string) {
  const suyas = lista.filter((x) => x.empresaId === empresaId).sort((x, y) => Date.parse(y.inicio ?? '0') - Date.parse(x.inicio ?? '0'));
  const vigentes = suyas.filter((x) => !x.saldo.vencida).sort((x, y) => (x.validez ? Date.parse(x.validez) : Infinity) - (y.validez ? Date.parse(y.validez) : Infinity));
  return vigentes[0] ?? suyas[0] ?? null;
}

type Grupo = 'atender' | 'activas' | 'vencidas';
/** Atender ya: en amarillo o rojo hace poco. Vencidas: vencio hace mas de 30 dias o se agoto y no se usa hace 60. */
export function grupoDe(b: BolsaConSaldo, ahora = Date.now()): Grupo {
  const ultimo = Math.max(0, ...b.movs.map((m) => Date.parse(m.fecha) || 0));
  if (b.saldo.vencida && (b.saldo.venceEn ?? 0) < -30) return 'vencidas';
  if (!b.saldo.vencida && b.saldo.restantes <= 0 && b.plan !== 'SUPPORT_12' && ahora - ultimo > 60 * DIA) return 'vencidas';
  return b.saldo.semaforo === 'verde' ? 'activas' : 'atender';
}

const GRUPOS: [Grupo, string][] = [
  ['atender', 'Atender ya'],
  ['activas', 'Activas'],
  ['vencidas', 'Vencidas o agotadas'],
];
const FILTROS: [Plan | 'TODAS', string][] = [
  ['TODAS', 'Todas'],
  ['SUPPORT_12', 'Support 12'],
  ['SUPPORT_6', 'Support 6'],
  ['EMERGENCY', 'Emergency'],
  ['ANTERIOR', 'Anteriores'],
];

const motivo = (b: BolsaConSaldo) => {
  if (b.saldo.vencida) return `venció el ${fechaCorta(b.validez!)}`;
  if (b.saldo.restantes <= 0) return b.plan === 'SUPPORT_12' ? 'sin horas este mes' : 'agotada';
  if (b.saldo.venceEn != null && b.saldo.venceEn <= 30) return `vence en ${b.saldo.venceEn} días`;
  return `queda ${horasTexto(b.saldo.restantes)}`;
};

export function Bolsas() {
  const qc = useQueryClient();
  const { yo, puede } = useSession();
  // Crear, editar, renovar y ajustar saldos (D99).
  const gestiona = puede('bolsas.gestionar');
  const r = useBolsas();
  const [sel, setSel] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');
  const [plan, setPlan] = useState<Plan | 'TODAS'>('TODAS');
  const [dialogo, setDialogo] = useState<{ tipo: 'nueva' | 'renovar' | 'editar' | 'consumo' | 'adicional'; bolsa?: BolsaConSaldo } | null>(null);
  const todas = useMemo(() => r.data ?? [], [r.data]);
  const lista = useMemo(
    () =>
      todas
        .filter((b) => plan === 'TODAS' || b.plan === plan)
        .filter((b) => !filtro || `${b.cliente} ${b.empresa ?? ''} ${b.factura}`.toLowerCase().includes(filtro.toLowerCase()))
        .sort((a, b) => (a.validez ? Date.parse(a.validez) : Infinity) - (b.validez ? Date.parse(b.validez) : Infinity)),
    [todas, plan, filtro],
  );
  const porGrupo = useMemo(() => {
    const g: Record<Grupo, BolsaConSaldo[]> = { atender: [], activas: [], vencidas: [] };
    for (const b of lista) g[grupoDe(b)].push(b);
    return g;
  }, [lista]);
  const actual = todas.find((b) => b.id === sel) ?? porGrupo.atender[0] ?? porGrupo.activas[0] ?? null;
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['bolsas'] });

  const ahora = new Date();
  const mesIni = new Date(ahora.getFullYear(), ahora.getMonth(), 1).getTime();
  const mesAntes = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1).getTime();
  const consumo = (desde: number, hasta: number) =>
    todas.flatMap((b) => b.movs).filter((m) => m.tipo === 'CONSUMO' && Date.parse(m.fecha) >= desde && Date.parse(m.fecha) < hasta).reduce((s, m) => s + m.minutos, 0);
  const esteMes = consumo(mesIni, Infinity);
  const mesPasado = consumo(mesAntes, mesIni);
  const grupos = todas.map((b) => grupoDe(b));
  const atender = todas.filter((_, i) => grupos[i] === 'atender');

  const hojas = () => [
    {
      nombre: 'Bolsas',
      filas: [
        ['Cliente', 'Empresa', 'Plan', 'Precio (USD)', 'Factura', 'Inicio', 'Vence', 'Contratado (h)', 'Usado (h)', 'Disponible (h)', 'Estado'],
        ...todas.map((b) => [
          b.cliente,
          b.empresa ?? '',
          nombrePlan(b.plan),
          b.plan === 'ANTERIOR' ? null : PLANES[b.plan].precio,
          b.factura,
          b.inicio ? new Date(b.inicio) : null,
          b.validez ? new Date(b.validez) : null,
          b.saldo.recargados / 60,
          b.saldo.consumidos / 60,
          b.saldo.restantes / 60,
          textoSaldo(b.saldo, b.plan),
        ]),
      ],
    },
    {
      nombre: 'Consumos',
      filas: [
        ['Fecha', 'Cliente', 'Plan', 'Servicio', 'Referencia', 'Atendió', 'Horario', 'Trabajado (min)', 'Descontado (min)'],
        ...todas.flatMap((b) =>
          b.movs
            .filter((m) => m.tipo === 'CONSUMO')
            .map((m) => [new Date(m.fecha), b.cliente, nombrePlan(b.plan), m.concepto, m.referencia, m.atendidoPor, HORARIOS.find((h) => h[0] === m.horario)?.[1] ?? '', m.minutosReales ?? m.minutos, m.minutos]),
        ),
      ],
    },
    {
      nombre: 'Horas adicionales',
      filas: [
        ['Fecha', 'Cliente', 'Horas', 'Valor (USD)', 'Factura', 'Registró'],
        ...todas.flatMap((b) => b.movs.filter((m) => esAdicional(m, b.plan)).map((m) => [new Date(m.fecha), b.cliente, m.minutos / 60, (m.minutos / 60) * HORA_ADICIONAL, m.referencia, m.atendidoPor])),
      ],
    },
  ];

  return (
    <div className="view bolsas">
      <header className="vhead">
        <div>
          <h1>Bolsas de soporte</h1>
          <p className="sub">Emergency, Support 6 y Support 12, como en QuickBooks. Las horas de los casos se descuentan solas.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <BotonExcel archivo={`bolsas-de-soporte-${hoyIso()}`} hojas={hojas} disabled={!todas.length} />
          {gestiona ? (
            <>
              <button className="btn" onClick={() => setDialogo({ tipo: 'consumo', bolsa: actual ?? undefined })} disabled={!todas.length}>
                <Icon name="clock" /> Consumo manual
              </button>
              <button className="btn primary" onClick={() => setDialogo({ tipo: 'nueva' })}>
                <Icon name="plus" /> Nueva bolsa
              </button>
            </>
          ) : null}
        </div>
      </header>
      {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
      {r.isLoading ? (
        <Skeleton rows={8} />
      ) : todas.length ? (
        <>
          <section className="kpis k5" aria-label="Resumen">
            <Kpi label="Clientes con bolsa" value={new Set(todas.map((b) => b.empresaId ?? b.cliente)).size} detail={`${todas.length} bolsas en total`} />
            <Kpi label="Activas" value={grupos.filter((g) => g === 'activas').length} detail="con vigencia y saldo" />
            <Kpi label="Atender ya" value={atender.length} detail="por vencer, por agotarse o recién vencidas" tone={atender.length ? 'warn' : undefined} />
            <Kpi label="Vencidas o agotadas" value={grupos.filter((g) => g === 'vencidas').length} detail="sin vigencia o sin saldo" />
            <Kpi label="Soporte prestado este mes" value={horasTexto(esteMes)} detail={`${horasTexto(mesPasado)} el mes pasado`} />
          </section>
          {atender.length ? (
            <div className="bolsas-aviso" role="status">
              <b>{atender.length} para atender:</b>{' '}
              {atender
                .slice(0, 4)
                .map((b) => `${b.cliente} ${motivo(b)}`)
                .join(' · ')}
              {atender.length > 4 ? ` · y ${atender.length - 4} más` : ''}
            </div>
          ) : null}
          <div className="bolsas-cuerpo">
            <aside className="bolsas-lista" aria-label="Bolsas">
              <input className="inp" placeholder="Buscar cliente o factura…" value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Buscar cliente o factura" />
              <div className="seg" role="group" aria-label="Plan">
                {FILTROS.map(([v, t]) => (
                  <button key={v} aria-pressed={plan === v} onClick={() => setPlan(v)}>
                    {t}
                  </button>
                ))}
              </div>
              {GRUPOS.map(([g, t]) =>
                porGrupo[g].length ? (
                  <section key={g}>
                    <h2 className="lbl">
                      {t} <span className="muted">{porGrupo[g].length}</span>
                    </h2>
                    {porGrupo[g].map((b) => (
                      <button key={b.id} className={`bolsa-it ${TONO[b.saldo.semaforo]}`} aria-current={actual?.id === b.id} onClick={() => setSel(b.id)}>
                        <span>
                          <b>{b.cliente}</b>
                          <small>
                            {nombrePlan(b.plan)} · {motivo(b)}
                          </small>
                        </span>
                        <span className="mono">{horasTexto(Math.max(0, b.saldo.restantes))}</span>
                      </button>
                    ))}
                  </section>
                ) : null,
              )}
              {!lista.length ? <p className="note">Nada con ese filtro.</p> : null}
            </aside>
            {actual ? <Detalle b={actual} onAccion={(tipo) => setDialogo({ tipo, bolsa: actual })} onCambio={refrescar} /> : <Empty icon="clock" titulo="Elige una bolsa" texto="Su detalle aparece aquí." />}
          </div>
        </>
      ) : (
        <Empty icon="clock" titulo="Sin bolsas de soporte" texto="Crea la bolsa de un cliente con Nueva bolsa." />
      )}
      {dialogo?.tipo === 'nueva' || dialogo?.tipo === 'renovar' ? (
        <NuevaBolsa renovar={dialogo.tipo === 'renovar' ? dialogo.bolsa : undefined} onClose={() => setDialogo(null)} onListo={(id) => (refrescar(), setSel(id))} quien={yo.nombre} />
      ) : null}
      {dialogo?.tipo === 'editar' && dialogo.bolsa ? <EditarBolsa bolsa={dialogo.bolsa} onClose={() => setDialogo(null)} onListo={refrescar} /> : null}
      {dialogo?.tipo === 'adicional' && dialogo.bolsa ? <HorasAdicionales bolsa={dialogo.bolsa} onClose={() => setDialogo(null)} onListo={refrescar} quien={yo.nombre} /> : null}
      {dialogo?.tipo === 'consumo' ? <ConsumoManual bolsas={todas.filter((b) => !b.saldo.vencida)} inicial={dialogo.bolsa} onClose={() => setDialogo(null)} onListo={refrescar} quien={yo.nombre} /> : null}
    </div>
  );
}

function Detalle({ b, onAccion, onCambio }: { b: BolsaConSaldo; onAccion: (t: 'renovar' | 'editar' | 'consumo' | 'adicional') => void; onCambio: () => void }) {
  const gestiona = useSession().puede('bolsas.gestionar');
  const s12 = b.plan === 'SUPPORT_12';
  // En Support 12 la tabla tambien muestra las horas adicionales compradas (son las unicas recargas del plan).
  const consumos = b.movs.filter((m) => m.tipo === 'CONSUMO' || esAdicional(m, b.plan));
  const quitar = async (m: Movimiento) => {
    try {
      await borrarMovimiento(m.id);
      toast(m.tipo === 'RECARGA' ? 'Horas adicionales borradas.' : 'Consumo borrado.');
      onCambio();
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const vencidos = (b.saldo.meses ?? []).reduce((s, m) => s + m.vencidos, 0);
  const mesesRestantes = s12 && b.validez ? Math.max(0, (b.saldo.meses ? 12 - b.saldo.meses.length : 12)) : null;
  return (
    <section className="bolsa-det" aria-label={`Bolsa de ${b.cliente}`}>
      <header className="vhead">
        <div>
          <h2>{b.cliente}</h2>
          <p className="sub">
            <span className={`plan-tag ${b.plan.toLowerCase()}`}>{nombrePlan(b.plan)}</span> {b.plan !== 'ANTERIOR' ? `${PLANES[b.plan].detalle} · ${usd(PLANES[b.plan].precio)} · ` : ''}
            {b.inicio ? fechaLarga(b.inicio) : 'sin inicio'} – {b.validez ? fechaLarga(b.validez) : 'sin vencimiento'}
            {b.empresa && b.empresa !== b.cliente ? ` · ${b.empresa}` : ''}
          </p>
          <p className="sub">
            {b.factura ? `Factura ${b.factura}` : 'Sin factura registrada'}
            {b.documento ? (
              <>
                {' · '}
                <a href={b.documento} target="_blank" rel="noreferrer">
                  Ver documento <Icon name="external" />
                </a>
              </>
            ) : null}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} hidden={!gestiona}>
          <button className="btn sm" onClick={() => onAccion('editar')}>
            <Icon name="edit" /> Editar
          </button>
          <button className="btn sm" onClick={() => onAccion('renovar')}>
            <Icon name="refresh" /> Renovar
          </button>
          {s12 && ADICIONAL_S12 && !b.saldo.vencida && b.saldo.mes ? (
            <button className="btn sm" onClick={() => onAccion('adicional')} title={`Producto «Hora adicional» de QuickBooks, ${usd(HORA_ADICIONAL)} c/u`}>
              <Icon name="plus" /> Hora adicional
            </button>
          ) : null}
          <button className="btn sm primary" onClick={() => onAccion('consumo')} disabled={b.saldo.vencida}>
            <Icon name="plus" /> Consumo
          </button>
        </div>
      </header>
      <div className="bolsa-franja">
        <div>
          <span>{s12 ? 'Disponible este mes' : 'Disponible'}</span>
          <strong className={b.saldo.semaforo === 'rojo' ? 'bad-ink' : b.saldo.semaforo === 'amarillo' ? 'warn-ink' : ''}>{horasTexto(Math.max(0, b.saldo.restantes))}</strong>
        </div>
        <div>
          <span>{s12 ? 'Usado este mes' : 'Usado'}</span>
          <strong>{horasTexto(b.saldo.consumidos)}</strong>
        </div>
        {s12 ? (
          <>
            <div>
              <span>Vencido sin usar</span>
              <strong>{horasTexto(vencidos)}</strong>
            </div>
            <div>
              <span>Meses restantes</span>
              <strong>{mesesRestantes ?? '—'}</strong>
            </div>
          </>
        ) : (
          <div>
            <span>Contratado</span>
            <strong>{horasTexto(b.saldo.recargados)}</strong>
          </div>
        )}
        <div>
          <span>Estado</span>
          <strong style={{ fontSize: 14 }}>
            <PillSaldo saldo={b.saldo} plan={b.plan} />
          </strong>
        </div>
      </div>
      {s12 && b.saldo.meses?.length ? (
        <div className="bolsa-meses" aria-label="Uso mes a mes">
          {b.saldo.meses.map((m) => (
            <div key={m.desde} title={`${fechaCorta(m.desde)} – ${fechaCorta(m.hasta - 1)}: ${horasTexto(m.usados)} de ${horasTexto(m.disponibles)}`}>
              <span className="bolsa-mes-barra">
                <i style={{ height: `${Math.min(100, (m.usados / Math.max(1, m.disponibles)) * 100)}%` }} className={m.usados > m.disponibles ? 'bad' : ''} />
              </span>
              <small>{new Date(m.desde).toLocaleDateString('es-CO', { month: 'short', timeZone: 'America/Bogota' })}</small>
            </div>
          ))}
        </div>
      ) : null}
      {b.notas ? <p className="note">{b.notas}</p> : null}
      <div className="tabla-w">
        <table className="tabla">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Servicio</th>
              <th>Horario</th>
              <th className="num">Trabajado</th>
              <th className="num">Descontado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {consumos.map((m) =>
              m.tipo === 'RECARGA' ? (
                <tr key={m.id}>
                  <td className="mono">{fechaCorta(m.fecha)}</td>
                  <td>
                    {m.concepto}
                    <small className="muted" style={{ display: 'block' }}>
                      {[m.referencia ? `Factura ${m.referencia}` : 'Sin factura', m.atendidoPor].filter(Boolean).join(' · ')}
                    </small>
                  </td>
                  <td>
                    <Pill tone="ok">Adicional · {usd((m.minutos / 60) * HORA_ADICIONAL)}</Pill>
                  </td>
                  <td className="num muted">—</td>
                  <td className="num">+{horasTexto(m.minutos)}</td>
                  <td>
                    <button className="btn sm ghost" title="Borrar estas horas adicionales" aria-label="Borrar horas adicionales" onClick={() => void quitar(m)}>
                      <Icon name="trash" />
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={m.id}>
                  <td className="mono">{fechaCorta(m.fecha)}</td>
                  <td>
                    {m.concepto}
                    <small className="muted" style={{ display: 'block' }}>
                      {[m.referencia, m.atendidoPor].filter(Boolean).join(' · ')}
                    </small>
                  </td>
                  <td>{m.horario && m.horario !== 'NORMAL' ? <Pill tone="warn">{HORARIOS.find((h) => h[0] === m.horario)?.[1]}</Pill> : <span className="muted">Normal</span>}</td>
                  <td className="num">{horasTexto(m.minutosReales ?? m.minutos)}</td>
                  <td className="num">{horasTexto(m.minutos)}</td>
                  <td>
                    {!m.registroHorasId ? (
                      <button className="btn sm ghost" title="Borrar este consumo manual" aria-label="Borrar consumo" onClick={() => void quitar(m)}>
                        <Icon name="trash" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ),
            )}
            {!consumos.length ? (
              <tr>
                <td colSpan={6} className="muted">
                  Sin consumos todavía.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const useEmpresas = () => useQuery({ queryKey: ['empresas', 'nombres'], queryFn: () => listAll<{ id: string; name: string }>('companies', { orderBy: 'name[AscNullsLast]' }, 3000), staleTime: 5 * 60_000 });

function NuevaBolsa({ renovar, onClose, onListo, quien }: { renovar?: BolsaConSaldo; onClose: () => void; onListo: (id: string) => void; quien: string }) {
  // Renovar: mismo cliente y plan, desde el dia siguiente al vencimiento (o hoy si ya vencio).
  const desdeRenovar = () => {
    if (!renovar?.validez || renovar.saldo.vencida) return hoyIso();
    const d = new Date(Date.parse(renovar.validez) + DIA);
    return d.toISOString().slice(0, 10);
  };
  const [v, setV] = useState({
    empresaId: renovar?.empresaId ?? '',
    cliente: renovar?.cliente ?? '',
    plan: (renovar && renovar.plan !== 'ANTERIOR' ? renovar.plan : 'SUPPORT_6') as Exclude<Plan, 'ANTERIOR'>,
    inicio: renovar ? desdeRenovar() : hoyIso(),
    factura: '',
    documento: '',
    notas: '',
  });
  const [guardando, setGuardando] = useState(false);
  const empresas = useEmpresas();
  const p = PLANES[v.plan];
  const vence = v.inicio ? vigenciaDe(v.plan, aIso(v.inicio)) : null;
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const b = await crearBolsaPlan({ ...v, empresaId: v.empresaId || null, inicio: aIso(v.inicio), quien });
      toast(`${p.nombre} creado para ${v.cliente}.`);
      onListo(b.id);
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo={renovar ? `Renovar · ${renovar.cliente}` : 'Nueva bolsa de soporte'} onClose={onClose} width={620}>
      <form className="form" onSubmit={guardar}>
        <label>
          Empresa
          <select value={v.empresaId} onChange={(e) => setV({ ...v, empresaId: e.target.value, cliente: v.cliente || (empresas.data?.find((x) => x.id === e.target.value)?.name ?? '') })} required>
            <option value="">Elige la empresa…</option>
            {(empresas.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nombre de la bolsa
          <input value={v.cliente} onChange={(e) => setV({ ...v, cliente: e.target.value })} required />
        </label>
        <div className="planes-qb" role="radiogroup" aria-label="Plan">
          {(Object.keys(PLANES) as Exclude<Plan, 'ANTERIOR'>[]).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={v.plan === k} className="plan-qb" onClick={() => setV({ ...v, plan: k })}>
              <b>{PLANES[k].nombre}</b>
              <span className="mono">{usd(PLANES[k].precio)}</span>
              <small>{PLANES[k].detalle}</small>
            </button>
          ))}
        </div>
        <div className="grid3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
          <label>
            Desde
            <SelectorFecha value={v.inicio} onChange={(x) => setV({ ...v, inicio: x })} required etiqueta="Desde" />
          </label>
          <label>
            Vence (sale del plan)
            <input value={vence ? fechaLarga(vence) : ''} readOnly />
          </label>
        </div>
        <div className="grid3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)' }}>
          <label>
            Factura de QuickBooks u orden
            <input value={v.factura} onChange={(e) => setV({ ...v, factura: e.target.value })} placeholder="Ej. 1102" />
          </label>
          <label>
            Enlace del documento (opcional)
            <input type="url" value={v.documento} onChange={(e) => setV({ ...v, documento: e.target.value })} placeholder="https://… factura o comprobante" />
          </label>
        </div>
        <label>
          Notas
          <input value={v.notas} onChange={(e) => setV({ ...v, notas: e.target.value })} placeholder="Condiciones, referencia de pago…" />
        </label>
        <p className="note">
          Se carga: {p.mensual ? `${horasTexto(p.minutos)} cada mes durante 12 meses (no se acumulan)` : horasTexto(p.minutos)} · {usd(p.precio)} · producto «{p.nombre}» de QuickBooks.
        </p>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : renovar ? 'Renovar' : 'Crear bolsa'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function EditarBolsa({ bolsa, onClose, onListo }: { bolsa: BolsaConSaldo; onClose: () => void; onListo: () => void }) {
  const [v, setV] = useState({ cliente: bolsa.cliente, factura: bolsa.factura, documento: bolsa.documento, notas: bolsa.notas });
  const [guardando, setGuardando] = useState(false);
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await update('bolsasHoras', bolsa.id, { name: v.cliente.trim(), factura: v.factura.trim(), documento: v.documento.trim(), notas: v.notas });
      toast('Bolsa actualizada.');
      onListo();
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo={`Editar · ${bolsa.cliente}`} subtitulo="El plan, las horas y las fechas no se cambian: si hay un error, crea la bolsa de nuevo." onClose={onClose} width={520}>
      <form className="form" onSubmit={guardar}>
        <label>
          Nombre de la bolsa
          <input value={v.cliente} onChange={(e) => setV({ ...v, cliente: e.target.value })} required />
        </label>
        <label>
          Factura de QuickBooks u orden
          <input value={v.factura} onChange={(e) => setV({ ...v, factura: e.target.value })} />
        </label>
        <label>
          Enlace del documento
          <input type="url" value={v.documento} onChange={(e) => setV({ ...v, documento: e.target.value })} placeholder="https://…" />
        </label>
        <label>
          Notas
          <textarea value={v.notas} onChange={(e) => setV({ ...v, notas: e.target.value })} rows={3} />
        </label>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Soporte prestado sin caso (llamada, visita): se descuenta con el minimo de 20 min y el recargo del horario. */
function ConsumoManual({ bolsas: lista, inicial, onClose, onListo, quien }: { bolsas: BolsaConSaldo[]; inicial?: BolsaConSaldo; onClose: () => void; onListo: () => void; quien: string }) {
  const [v, setV] = useState({ bolsaId: inicial && !inicial.saldo.vencida ? inicial.id : '', fecha: hoyIso(), horas: '0', min: '30', concepto: '', horario: horarioSugerido() as Horario });
  const [guardando, setGuardando] = useState(false);
  const real = Number(v.horas || 0) * 60 + Number(v.min || 0);
  const cobra = minutosACobrar(real, v.horario);
  const b = lista.find((x) => x.id === v.bolsaId);
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (!b) return toast('Elige la bolsa.', 'info');
    if (real <= 0) return toast('Escribe cuánto tiempo se trabajó.', 'info');
    setGuardando(true);
    try {
      await crearMovimiento({ bolsaId: b.id, tipo: 'CONSUMO', minutos: cobra, minutosReales: real, horario: v.horario, fecha: aIso(v.fecha), concepto: v.concepto, atendidoPor: quien, referencia: '', casoId: null, registroHorasId: null });
      toast(`Se descontaron ${horasTexto(cobra)} de ${b.cliente}.`);
      onListo();
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo="Consumo manual" subtitulo="Soporte que no quedó en un caso. Lo de los casos se descuenta solo al registrar horas." onClose={onClose} width={560}>
      <form className="form" onSubmit={guardar}>
        <label>
          Bolsa
          <select value={v.bolsaId} onChange={(e) => setV({ ...v, bolsaId: e.target.value })} required>
            <option value="">Elige la bolsa…</option>
            {lista.map((x) => (
              <option key={x.id} value={x.id}>
                {x.cliente} · {nombrePlan(x.plan)} · {textoSaldo(x.saldo, x.plan).toLowerCase()}
              </option>
            ))}
          </select>
        </label>
        <label>
          ¿Qué se hizo?
          <input value={v.concepto} onChange={(e) => setV({ ...v, concepto: e.target.value })} required placeholder="Llamada de soporte, revisión remota…" />
        </label>
        <div className="grid3" style={{ gridTemplateColumns: '90px 90px minmax(0,1fr)' }}>
          <label>
            Horas
            <input type="number" min={0} max={24} value={v.horas} onChange={(e) => setV({ ...v, horas: e.target.value })} />
          </label>
          <label>
            Minutos
            <input type="number" min={0} max={59} value={v.min} onChange={(e) => setV({ ...v, min: e.target.value })} />
          </label>
          <label>
            Día
            <SelectorFecha value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} required etiqueta="Día del servicio" />
          </label>
        </div>
        <div className="seg" role="group" aria-label="Horario del servicio">
          {HORARIOS.map(([h, t, d]) => (
            <button type="button" key={h} aria-pressed={v.horario === h} onClick={() => setV({ ...v, horario: h })} title={d}>
              {t}
            </button>
          ))}
        </div>
        <p className="note">
          Se descontarán <b>{horasTexto(cobra)}</b>
          {b ? ` · ${b.saldo.restantes - cobra >= 0 ? `quedan ${horasTexto(b.saldo.restantes - cobra)}` : `faltan ${horasTexto(cobra - Math.max(0, b.saldo.restantes))}`}` : ''}
          {ADICIONAL_S12 && b?.plan === 'SUPPORT_12' && b.saldo.restantes - cobra < 0 ? ` · véndele horas adicionales (${usd(HORA_ADICIONAL)} c/u) desde el detalle de la bolsa` : ''}
        </p>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Descontar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Horas adicionales de Support 12 (producto «Hora adicional» de QuickBooks, US$70 c/u): se suman al mes del plan de ese dia. */
function HorasAdicionales({ bolsa, onClose, onListo, quien }: { bolsa: BolsaConSaldo; onClose: () => void; onListo: () => void; quien: string }) {
  const [v, setV] = useState({ horas: '1', fecha: hoyIso(), factura: '' });
  const [guardando, setGuardando] = useState(false);
  const horas = Math.max(0, Math.floor(Number(v.horas) || 0));
  const t = Date.parse(aIso(v.fecha));
  const mes = (bolsa.saldo.meses ?? []).find((m) => m.desde <= t && t < m.hasta) ?? null;
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (horas < 1) return toast('Escribe cuántas horas compró.', 'info');
    if (!mes) return toast('Ese día no está dentro de un mes vigente del plan.', 'info');
    setGuardando(true);
    try {
      await comprarAdicionales({ bolsaId: bolsa.id, horas, fecha: aIso(v.fecha), factura: v.factura.trim(), quien });
      toast(`${horas} h adicional${horas > 1 ? 'es' : ''} para ${bolsa.cliente}.`);
      onListo();
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo={`Hora adicional · ${bolsa.cliente}`} subtitulo="Producto «Hora adicional» de QuickBooks. Se suma solo a ese mes del plan: lo que no se use no pasa al siguiente." onClose={onClose} width={520}>
      <form className="form" onSubmit={guardar}>
        <div className="grid3" style={{ gridTemplateColumns: '110px minmax(0,1fr)' }}>
          <label>
            Horas
            <input type="number" min={1} max={50} step={1} value={v.horas} onChange={(e) => setV({ ...v, horas: e.target.value })} required />
          </label>
          <label>
            Día de la compra
            <SelectorFecha value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} required etiqueta="Día de la compra" />
          </label>
        </div>
        <label>
          Factura de QuickBooks u orden
          <input value={v.factura} onChange={(e) => setV({ ...v, factura: e.target.value })} placeholder="Ej. 1150" />
        </label>
        <p className="note">
          {mes ? (
            <>
              Se suman <b>{horasTexto(horas * 60)}</b> al mes {fechaCorta(mes.desde)} – {fechaCorta(mes.hasta - 1)} (quedarían {horasTexto(Math.max(0, mes.disponibles - mes.usados) + horas * 60)}) ·{' '}
              <b>{usd(horas * HORA_ADICIONAL)}</b> a facturar.
            </>
          ) : (
            'Ese día no está dentro de un mes vigente del plan.'
          )}
        </p>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando || !mes}>
            {guardando ? 'Guardando…' : 'Agregar horas'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
